import { useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Page, Text } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../app/stores/store';

// Never accept a query-string ticket: it would reach access logs/referrers.
// Strip both URL components before making a request or displaying any error.
export function takeLegacyPilotCode(): string | null {
  const fragment = new URLSearchParams(window.location.hash.slice(1));
  const query = new URLSearchParams(window.location.search);
  window.history.replaceState(window.history.state, '', window.location.pathname);
  const codes = fragment.getAll('code');
  if (query.has('code') || codes.length !== 1 || !/^[A-Za-z0-9_-]{32,512}$/.test(codes[0])) return null;
  return codes[0];
}

export default function LegacyPilotHandoffPage({ operator = false }: { operator?: boolean }) {
  const { userStore } = useStore();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    // React development effect replay must not consume a single-use ticket twice.
    if (started.current) return;
    started.current = true;
    const code = takeLegacyPilotCode();
    if (!code) {
      userStore.logout();
      setError(operator ? 'This operator link is missing or invalid. Open a new session from the console.' : 'This handoff link is missing or invalid. Open the app again from the PFV1 store admin to obtain a new link.');
      return;
    }
    void (operator ? userStore.legacyPilotLogin(code, true) : userStore.legacyPilotLogin(code)).then(() => navigate('/dashboard', { replace: true })).catch(() => {
      setError(operator ? 'Operator access could not be started. The link expired, was used, or the store changed. Open a new session from the console.' : 'The handoff could not be completed. The link may have expired, already been used, or the store route may have changed. Open the app again from the PFV1 store admin; do not reuse this link.');
    });
  }, [navigate, userStore, operator]);
  return <Page title="Opening your store dashboard"><BlockStack gap="400">
    {error ? <Banner tone="critical" title="Store session not started">{error}</Banner>
      : <Text as="p">Verifying your one-time store handoff…</Text>}
    <Text as="p" tone="subdued">{operator ? 'Opening a time-limited operator session. Changes affect the live store and are audited.' : 'This handoff uses your authenticated PFV1 session. It does not install another Shopify app or start an OAuth flow.'}</Text>
  </BlockStack></Page>;
}
